import * as React from 'react';

import type { AskMessage, PersistedAiAskState } from '../types';
import { readAccessTokenCookie } from '../utils/auth';

export function useSavedChat(userId: string, tenantId: string) {
  const [askMessages, setAskMessages] = React.useState<AskMessage[]>([]);
  const [askSessionId, setAskSessionId] = React.useState('');
  const [askError, setAskError] = React.useState('');

  const storageKey = React.useMemo(() => {
    if (!userId) return '';
    return `ai-ask:${tenantId || 'tenant'}:${userId}`;
  }, [tenantId, userId]);

  const prevUserIdRef = React.useRef(userId);
  const prevStorageKeyRef = React.useRef(storageKey);

  React.useEffect(() => {
    if (storageKey) {
      prevStorageKeyRef.current = storageKey;
    }
  }, [storageKey]);

  React.useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      const previousUserId = prevUserIdRef.current;
      prevUserIdRef.current = userId;
      if (!userId && previousUserId && typeof window !== 'undefined') {
        try {
          if (prevStorageKeyRef.current) {
            window.localStorage.removeItem(prevStorageKeyRef.current);
          }
        } catch {
          // Ignore localStorage remove errors
        }
      }
      setAskSessionId('');
      setAskMessages([]);
      setAskError('');
    }
  }, [userId]);

  React.useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<PersistedAiAskState>;
      const persistedToken =
        typeof parsed.authSessionToken === 'string'
          ? parsed.authSessionToken
          : '';
      const currentToken = readAccessTokenCookie();
      if (persistedToken && currentToken && persistedToken !== currentToken) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      setAskMessages(
        Array.isArray(parsed.askMessages) ? parsed.askMessages : []
      );
      setAskSessionId(
        typeof parsed.askSessionId === 'string' ? parsed.askSessionId : ''
      );
      setAskError(typeof parsed.askError === 'string' ? parsed.askError : '');
    } catch {}
  }, [storageKey]);

  React.useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    const payload: PersistedAiAskState = {
      askMessages,
      askSessionId,
      askError,
      authSessionToken: readAccessTokenCookie(),
    };
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {}
  }, [askError, askMessages, askSessionId, storageKey]);

  const clearPersistedChat = React.useCallback(() => {
    setAskSessionId('');
    setAskMessages([]);
    setAskError('');
    if (storageKey && typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(storageKey);
      } catch {}
    }
  }, [storageKey]);

  return {
    askMessages,
    setAskMessages,
    askSessionId,
    setAskSessionId,
    askError,
    setAskError,
    clearPersistedChat,
  };
}
